import { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faPen, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import type { ReportSchedule } from '../../../types/bi';
import {
  useScheduleList,
  useCreateSchedule,
  useUpdateSchedule,
  useDeleteSchedule,
} from '../hooks/useDashboards';
import styles from './ScheduleDialog.module.css';

/* ─── Cron presets ─────────────────────────────────────────────────────────── */

const PRESETS = [
  { label: 'Daily at 8 AM', cron: '0 8 * * *' },
  { label: 'Weekly Monday 8 AM', cron: '0 8 * * 1' },
  { label: 'Monthly 1st at 8 AM', cron: '0 8 1 * *' },
  { label: 'Every weekday at 8 AM', cron: '0 8 * * 1-5' },
  { label: 'Custom', cron: '' },
] as const;

interface ScheduleDialogProps {
  dashboardId: number;
  dashboardName: string;
  onClose: () => void;
}

export default function ScheduleDialog({
  dashboardId,
  dashboardName,
  onClose,
}: ScheduleDialogProps) {
  const { data: scheduleData } = useScheduleList(dashboardId);
  const createMut = useCreateSchedule(dashboardId);
  const updateMut = useUpdateSchedule(dashboardId);
  const deleteMut = useDeleteSchedule(dashboardId);

  const schedules = scheduleData?.data ?? [];

  type Mode = 'list' | 'create' | 'edit';
  const [mode, setMode] = useState<Mode>(schedules.length === 0 ? 'create' : 'list');
  const [editId, setEditId] = useState<number | null>(null);

  // Form state
  const [preset, setPreset] = useState<string>(PRESETS[0].label);
  const [cronExpr, setCronExpr] = useState<string>(PRESETS[0].cron);
  const [format, setFormat] = useState<'pdf' | 'png' | 'csv'>('pdf');
  const [recipients, setRecipients] = useState<string[]>(['']);
  const [error, setError] = useState('');

  const resetForm = useCallback(() => {
    setPreset(PRESETS[0].label);
    setCronExpr(PRESETS[0].cron);
    setFormat('pdf');
    setRecipients(['']);
    setError('');
    setEditId(null);
  }, []);

  const openEdit = useCallback((s: ReportSchedule) => {
    setEditId(s.id);
    const match = PRESETS.find((p) => p.cron === s.cron_expression);
    setPreset(match ? match.label : 'Custom');
    setCronExpr(s.cron_expression);
    setFormat(s.format);
    setRecipients(s.recipients.map((r) => r.email));
    setError('');
    setMode('edit');
  }, []);

  const handlePresetChange = useCallback((label: string) => {
    setPreset(label);
    const found = PRESETS.find((p) => p.label === label);
    if (found && found.cron) setCronExpr(found.cron);
  }, []);

  const addRecipient = useCallback(() => setRecipients((r) => [...r, '']), []);
  const removeRecipient = useCallback(
    (i: number) => setRecipients((r) => r.filter((_, idx) => idx !== i)),
    [],
  );
  const updateRecipient = useCallback(
    (i: number, val: string) => setRecipients((r) => r.map((v, idx) => (idx === i ? val : v))),
    [],
  );

  const validate = useCallback(() => {
    if (!cronExpr.trim()) return 'Cron expression is required';
    const parts = cronExpr.trim().split(/\s+/);
    if (parts.length < 5 || parts.length > 6) return 'Cron expression must have 5 or 6 fields';
    const validEmails = recipients.filter((e) => e.trim());
    if (validEmails.length === 0) return 'At least one recipient is required';
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const em of validEmails) {
      if (!emailRe.test(em.trim())) return `Invalid email: ${em}`;
    }
    return '';
  }, [cronExpr, recipients]);

  const handleSave = useCallback(() => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }

    const body = {
      cron_expression: cronExpr.trim(),
      recipients: recipients.filter((e) => e.trim()).map((e) => ({ email: e.trim() })),
      format,
    };

    const opts = {
      onSuccess: () => {
        resetForm();
        setMode('list');
      },
      onError: (e: Error) => setError(e.message),
    };

    if (mode === 'edit' && editId) {
      updateMut.mutate({ scheduleId: editId, ...body } as never, opts);
    } else {
      createMut.mutate(body, opts);
    }
  }, [validate, cronExpr, recipients, format, mode, editId, createMut, updateMut, resetForm]);

  const handleDelete = useCallback(
    (id: number) => {
      if (!window.confirm('Delete this schedule?')) return;
      deleteMut.mutate(id);
    },
    [deleteMut],
  );

  const saving = createMut.isPending || updateMut.isPending;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>Scheduled Reports — {dashboardName}</h2>
          <button className={styles.closeBtn} onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {mode === 'list' && (
          <>
            {schedules.length === 0 ? (
              <div className={styles.empty}>
                No schedules yet.
                <br />
                <button
                  className={styles.addBtn}
                  onClick={() => {
                    resetForm();
                    setMode('create');
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} /> Create schedule
                </button>
              </div>
            ) : (
              <div className={styles.scheduleList}>
                {schedules.map((s) => (
                  <div key={s.id} className={styles.scheduleCard}>
                    <div className={styles.scheduleInfo}>
                      <strong>
                        {s.cron_expression} — {s.format.toUpperCase()}
                      </strong>
                      <span>{s.recipients.map((r) => r.email).join(', ')}</span>
                      <span>
                        {s.is_active ? 'Active' : 'Paused'}
                        {s.last_sent_at
                          ? ` · Last sent ${new Date(s.last_sent_at).toLocaleDateString()}`
                          : ''}
                      </span>
                    </div>
                    <div className={styles.scheduleActions}>
                      <button className={styles.removeBtn} onClick={() => openEdit(s)} title="Edit">
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                      <button
                        className={styles.removeBtn}
                        onClick={() => handleDelete(s.id)}
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  className={styles.addBtn}
                  onClick={() => {
                    resetForm();
                    setMode('create');
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add schedule
                </button>
              </div>
            )}
          </>
        )}

        {(mode === 'create' || mode === 'edit') && (
          <>
            <div className={styles.body}>
              <div className={styles.field}>
                <span className={styles.label}>Frequency</span>
                <select
                  className={styles.select}
                  value={preset}
                  onChange={(e) => handlePresetChange(e.target.value)}
                >
                  {PRESETS.map((p) => (
                    <option key={p.label} value={p.label}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              {preset === 'Custom' && (
                <div className={styles.field}>
                  <span className={styles.label}>Cron expression</span>
                  <input
                    className={styles.input}
                    placeholder="0 8 * * 1"
                    value={cronExpr}
                    onChange={(e) => setCronExpr(e.target.value)}
                  />
                  <span className={styles.hint}>minute hour day-of-month month day-of-week</span>
                </div>
              )}

              <div className={styles.field}>
                <span className={styles.label}>Format</span>
                <select
                  className={styles.select}
                  value={format}
                  onChange={(e) => setFormat(e.target.value as 'pdf' | 'png' | 'csv')}
                >
                  <option value="pdf">PDF</option>
                  <option value="png">PNG</option>
                  <option value="csv">CSV</option>
                </select>
              </div>

              <div className={styles.field}>
                <span className={styles.label}>Recipients</span>
                {recipients.map((email, i) => (
                  <div key={i} className={styles.recipientRow}>
                    <input
                      className={styles.input}
                      type="email"
                      placeholder="user@example.com"
                      value={email}
                      onChange={(e) => updateRecipient(i, e.target.value)}
                    />
                    {recipients.length > 1 && (
                      <button className={styles.removeBtn} onClick={() => removeRecipient(i)}>
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    )}
                  </div>
                ))}
                <button className={styles.addBtn} onClick={addRecipient}>
                  <FontAwesomeIcon icon={faPlus} /> Add recipient
                </button>
              </div>
            </div>

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.footer}>
              <button
                className={styles.cancelBtn}
                onClick={() => {
                  resetForm();
                  setMode('list');
                }}
              >
                Cancel
              </button>
              <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : mode === 'edit' ? 'Update schedule' : 'Create schedule'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
